import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, Phone, X } from 'lucide-react';
import { QuoteButton } from './QuoteModal';

/**
 * Sticky side CTA — visible on every page.
 *
 * Visibility rules:
 *   • Home page: wait until the user scrolls past the "About us / GWM SEO
 *     Sydney" section (element #about-us-section), then stay visible until
 *     the footer is in view.
 *   • Every other page: appear after ~600 px of scroll and hide once the
 *     footer is in view.
 *
 * Accessibility:
 *   • When the CTA is hidden we set `inert` + `tabIndex={-1}` on the
 *     interactive descendants so screen readers and keyboard users don't
 *     land on invisible controls, and we omit aria-hidden while visible
 *     (the elements are fully interactive).
 */
const FloatingSideCTA = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('gwm-float-cta-dismissed') === '1') {
      setDismissed(true);
    }
  }, []);

  useEffect(() => {
    if (dismissed) return undefined;

    const isHome = pathname === '/';
    const findTrigger = () =>
      isHome ? document.getElementById('about-us-section') : null;
    const findFooter = () => document.querySelector('footer');

    let trigger = findTrigger();
    let footer = findFooter();

    const compute = () => {
      trigger = trigger || findTrigger();
      footer = footer || findFooter();

      // Hide when footer enters the viewport (so we don't sit on top of it)
      const footerRect = footer ? footer.getBoundingClientRect() : null;
      const footerVisible =
        footerRect && footerRect.top < window.innerHeight - 20;
      if (footerVisible) {
        setVisible(false);
        return;
      }

      if (isHome) {
        if (!trigger) {
          setVisible(false);
          return;
        }
        const triggerRect = trigger.getBoundingClientRect();
        setVisible(triggerRect.top < window.innerHeight * 0.3);
      } else {
        // On every other page, appear after the reader has committed to scrolling
        setVisible(window.scrollY > 600);
      }
    };

    compute();
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, [dismissed, pathname]);

  if (dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem('gwm-float-cta-dismissed', '1');
    setDismissed(true);
  };

  // When hidden, make descendants unreachable by keyboard + screen reader.
  const hiddenAttrs = visible ? {} : { 'aria-hidden': 'true', inert: '' };
  const focusAttr = visible ? 0 : -1;

  return (
    <aside
      {...hiddenAttrs}
      aria-label="Quick contact options"
      className={`fixed z-[55] right-3 md:right-4 top-1/2 -translate-y-1/2 transition-all duration-300 ${
        visible
          ? 'opacity-100 translate-x-0 pointer-events-auto'
          : 'opacity-0 translate-x-6 pointer-events-none'
      }`}
      data-testid="floating-side-cta"
    >
      <div className="relative">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss quick contact buttons"
          tabIndex={focusAttr}
          className="absolute -top-2 -left-2 z-10 w-6 h-6 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:scale-110 transition-all"
          data-testid="floating-side-cta-dismiss"
        >
          <X size={12} />
        </button>

        <div className="flex flex-col items-stretch gap-2 rounded-2xl bg-white shadow-2xl shadow-slate-900/20 border border-slate-200 p-2 w-[72px] md:w-[84px]">
          <QuoteButton
            data-testid="floating-side-cta-quote"
            tabIndex={focusAttr}
            aria-label="Get a FREE quote"
            className="group flex flex-col items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-[#db2777] to-[#be185d] hover:from-[#be185d] hover:to-[#9f1650] text-white py-4 px-2 text-[10.5px] md:text-[11px] font-bold leading-tight uppercase tracking-wider transition-all hover:-translate-y-0.5"
          >
            <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            <span className="text-center">
              Get<br />a FREE<br />Quote
            </span>
          </QuoteButton>
          <a
            href="tel:1800006296"
            data-testid="floating-side-cta-phone"
            tabIndex={focusAttr}
            aria-label="Call GWM on 1800 006 296"
            className="group flex flex-col items-center justify-center gap-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white py-4 px-2 text-[10.5px] md:text-[11px] font-bold leading-tight uppercase tracking-wider transition-all"
          >
            <Phone size={18} className="text-[#86efac]" aria-hidden="true" />
            <span className="text-center">
              Call<br />Us
            </span>
          </a>
        </div>
      </div>
    </aside>
  );
};

export default FloatingSideCTA;
